import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import {
	downloadEmVatReportRequest,
	getEmVatReportsListRequest,
} from 'redux/reducers/emVatReports/reducer';
import {
	getEmVatReportsList,
	getEmVatReportsListLoading,
} from 'redux/reducers/emVatReports/selectors';
import { TEmVatReportListItem } from 'redux/reducers/emVatReports/types';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { tableStructure } from './tableStructure';
import { ENavigateNames } from '../../routes/constants';

export const SalesReports: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);

	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);

	const emVatReportsList = useSelector(getEmVatReportsList);
	const emVatReportsListLoading = useSelector(getEmVatReportsListLoading);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEmVatReportListItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(
		currentTableStructure,
		navigateParams,
	) as TFilters;

	useEffect(() => {
		const orderBy: TOrderBy[] = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: tableStructure[+navigateParams.sortIndex]?.selector ?? 'id',
				direction:
					String(navigateParams?.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC,
			});
		}
		orderBy.push({
			fieldName: 'id',
			direction: ESortDirection.DESC,
		});

		dispatch(
			getEmVatReportsListRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	const handleAction = useCallback(
		(id: number, typeOfDialog: string) => {
			if (typeOfDialog === 'download') {
				const item = emVatReportsList?.data?.find((report) => report.id === id);
				if (item?.url) {
					const format = 'zip'; // item.url.split('/').pop() || 'csv';
					dispatch(
						downloadEmVatReportRequest({
							url: item.url,
							fileName: `em_vat_report_${item.id}.${format}`,
						}),
					);
				}
			}
		},
		[dispatch, emVatReportsList],
	);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<EasTableWithFilter<TEmVatReportListItem>
				tableStructure={currentTableStructure}
				items={emVatReportsList}
				itemsIsLoading={emVatReportsListLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						{ENavigateNames.SALES_REPORTS}
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
